import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import { SectionHead } from "./Reveal";
import { EASE } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value = 0, className = "w-4 h-4" }) => (
  <span className="flex gap-0.5" aria-label={`${value} von 5 Sternen`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= Math.round(value) ? "fill-[#E8B23A] text-[#E8B23A]" : "text-[#D5D8D0]"}`} />
    ))}
  </span>
);

const ReviewCard = ({ r, i }) => (
  <motion.article data-testid={`review-card-${i}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.08 }}
    className="rounded-[24px] border border-[#111511]/[0.08] bg-white p-7 flex flex-col">
    <Stars value={r?.rating ?? 0} />
    <p className="mt-5 text-[15px] leading-relaxed text-[#30352F] line-clamp-6 flex-1">{r?.text || "Bewertung ohne Text"}</p>
    <div className="mt-6 flex items-center gap-3">
      {r?.authorPhotoUri && <img src={r.authorPhotoUri} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />}
      <div className="min-w-0">
        {r?.authorUri ? (
          <a href={r.authorUri} target="_blank" rel="noreferrer" className="block font-medium text-[#111511] truncate hover:underline">{r?.authorName || "Google-Nutzer"}</a>
        ) : <span className="block font-medium text-[#111511] truncate">{r?.authorName || "Google-Nutzer"}</span>}
        <span className="block text-xs text-[#5B615A]">{r?.relativeTime} · Google</span>
      </div>
    </div>
  </motion.article>
);

export const Reviews = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!process.env.REACT_APP_BACKEND_URL) return undefined;
    let alive = true;
    axios.get(`${API}/reviews`).then((res) => { if (alive) setData(res.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
  const place = data?.place;
  if (!data?.configured || !place || reviews.length === 0) return null;

  return (
    <section id="bewertungen" data-testid="reviews-section" className="py-24 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <SectionHead eyebrow="Google-Bewertungen" lines={["Das sagen", <span className="text-[#5E8C3A]">unsere Kunden.</span>]} />
        <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="reviews-summary">
          <span className="font-head text-5xl font-semibold tracking-tight">{(place.rating ?? 0).toFixed(1).replace(".", ",")}</span>
          <div>
            <Stars value={place.rating ?? 0} className="w-5 h-5" />
            <p className="mt-1 text-sm text-[#5B615A]">{place.userRatingCount ?? 0} Bewertungen auf Google</p>
          </div>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.slice(0, 5).map((r, i) => <ReviewCard key={`${r?.publishTime}-${i}`} r={r} i={i} />)}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-xs text-[#5B615A]">Inhalte von Google Maps. Es werden bis zu 5 Bewertungen nach Relevanz angezeigt.</p>
          {place.googleMapsUri && (
            <a href={place.googleMapsUri} target="_blank" rel="noreferrer" data-testid="reviews-google-link"
              className="inline-flex items-center gap-2 rounded-full border border-[#111511]/15 px-5 py-3 text-sm font-medium text-[#111511] transition-colors duration-300 hover:bg-[#111511] hover:text-white">
              Alle Bewertungen auf Google <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
